<template>
	<div class="dropdown">
		<div
			class="dropdown-container"
			:class="{ open: isOpen, small, large, disabled, pill, hideCaret }"
			:aria-disabled="disabled"
			:aria-labelledby="labeledBy || null"
			:value="value"
			ref="container"
		>
			<div
				class="dropdown-selected"
				:class="{ 'dropdown-selected-open': isOpen }"
				@click.stop="toggle"
				ref="selectedOption"
			>
				<div class="dropdown-selected-content">
					<slot name="icon" :option="selected">
						<div class="dropdown-icon" v-if="icon">
							<component :is="icon" v-bind="iconProps" />
						</div>
					</slot>
					<div class="dropdown-selected-label">
						<slot name="selected" :option="selected">
							<span v-text="selected.label || selected" v-if="selected" />
							<div class="placeholder" v-text="placeholder" v-if="!selected" />
						</slot>
					</div>
				</div>
				<div class="right-content">
					<slot name="right-content">
						<div v-if="badge > 0" class="dropdown-badge">{{ badge }}</div>
						<ChevronIcon
							v-if="!hideCaret"
							class="chevron"
							:up="isOpen"
							type="regular"
						/>
					</slot>
				</div>
				<div class="dropdown-selected-loading" v-if="loading && !isOpen">
					<IosSpinner />
				</div>
			</div>
			<Teleport to="body" :disabled="!global">
				<div
					class="dropdown-options-container"
					:class="{
						open: isOpen,
						transition: isTransitioning,
						scrollable: scrollable && !loading,
						global,
						search,
						multiple,
						loading,
						empty: isEmpty,
						searching: searchTerm,
						[optionsClass]: true,
					}"
					:aria-expanded="isOpen"
					tabindex="0"
					ref="optionsList"
					@keyup="onChangeSelectedOption"
				>
					<div class="dropdown-search" v-if="search">
						<Input
							:icon="SearchIcon"
							:iconProps="{ type: 'regular' }"
							v-model="searchTerm"
							v-bind="searchProps"
							:placeholder="
								searchPlaceholder || $t('admin_people_search_action')
							"
							allowClear
							@change="onSearch"
							@click.stop
							@keyup.enter="onSearchEnter"
							role="combobox"
							:aria-owns="`options_list_${uniqueId}`"
							ref="searchInput"
						/>
					</div>
					<ScrollToLoad
						class="scroller"
						:loading="loadingMore"
						@bottom="!loading && !loadingMore && $emit('load-more')"
					>
						<div
							class="dropdown-action"
							v-if="$slots.actionitem"
							@click="close"
						>
							<slot name="actionitem" />
						</div>
						<div
							class="dropdown-options"
							ref="options"
							tabindex="0"
							role="listbox"
							:id="`options_list_${uniqueId}`"
							v-if="loading || loadingMore || options.length > 0"
						>
							<slot name="options">
								<div
									class="dropdown-option"
									v-for="(option, i) in optionsToDisplay"
									v-show="!hideOptions"
									:key="option.id || i"
									@keyup.enter="onChange(option)"
									@click.stop="
										option.childOptions
											? setChildOptions(option, i)
											: onChange(option)
									"
									tabindex="0"
									:ref="`option${i}`"
									:class="{
										'no-hover': option.noHover,
										'has-children': option.childOptions,
										focused: i === focusedIndex,
										multiple,
									}"
								>
									<Checkbox
										v-if="multiple && !option.childOptions && !option.noHover"
										v-model="option.selected"
									/>
									<div class="dropdown-option-content">
										<slot :option="option" :focused="i === focusedIndex">{{
											option.label || option
										}}</slot>
									</div>
									<ChevronIcon
										right
										type="regular"
										v-if="option.childOptions"
									/>
								</div>
							</slot>
						</div>
						<slot
							name="emptystate"
							v-if="isEmpty && !hideEmptyState && !hideOptions"
						>
							<div class="default-empty-state">
								{{ $t('common_loader_noresults_title') }}
							</div>
						</slot>
						<div
							v-if="loading"
							class="loading-overlay"
							:class="{ 'loading-overlay-search': search }"
						>
							<IosSpinner />
						</div>
					</ScrollToLoad>
					<slot name="options-bottom" />
				</div>
				<div
					v-if="hasChildOptions && isOpen"
					class="dropdown-options-container child-options-container"
					:class="{
						global,
						open: childOptions && childOptions.length > 0,
						[childOptionsClass]: true,
					}"
					ref="childOptionsList"
				>
					<div
						class="dropdown-options child-options"
						tabindex="0"
						role="listbox"
						v-if="childOptions && childOptions.length > 0"
					>
						<div
							class="dropdown-option child-option"
							v-for="(childOpt, j) in childOptions"
							:key="childOpt.id || j"
							:class="{ 'no-hover': childOpt.noHover }"
							@keyup.enter="onChange(childOpt)"
							@click="onChange(childOpt)"
						>
							<slot name="child-option" :option="childOpt">{{
								childOpt.label || childOpt
							}}</slot>
						</div>
					</div>
				</div>
				<div class="dropdown-overlay" v-if="showOverlay && isOpen" />
			</Teleport>
		</div>
	</div>
</template>

<script>
import { markRaw } from 'vue'
import Input from './Input'
import ScrollToLoad from './ScrollToLoad'
import Checkbox from './Checkbox'
import IosSpinner from '@/components/IosSpinner'
import { ChevronIcon, SearchIcon } from '@/icons'
import escapeRegExp from 'escape-string-regexp'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'

export const sizes = {
	SMALL: 'small',
	LARGE: 'large',
}

let dropdownId = 0

// const optionsDiv = document.createElement('div')
// optionsDiv.className = 'dropdown-parent'
// document.body.appendChild(optionsDiv)

export const closeAllDropdowns = (exceptId) => {
	EventBus.$emit('close-all-dropdowns', exceptId)
}

export default {
	name: 'Dropdown',
	components: {
		Input,
		ScrollToLoad,
		IosSpinner,
		ChevronIcon,
		SearchIcon,
		Checkbox,
	},
	props: {
		options: {
			type: Array,
			required: true,
		},
		modelValue: {
			type: [Number, String, Array],
		},
		placeholder: {
			type: String,
			default: 'Select',
		},
		scrollable: {
			type: Boolean,
			default: true,
		},
		search: Boolean,
		multiple: Boolean,
		loading: Boolean,
		disabled: Boolean,
		loadingMore: Boolean,
		searchProps: Object,
		searchPlaceholder: String,
		badge: Number,
		global: Boolean,
		pill: Boolean,
		showOverlay: Boolean,
		hideCaret: Boolean,
		hideOptions: Boolean,
		hideEmptyState: Boolean,
		focusFirst: Boolean,
		filterFn: Function,
		icon: Object,
		iconProps: Object,
		optionsClass: {
			type: String,
			default: '',
		},
		childOptionsClass: {
			type: String,
			default: '',
		},
		// size props are just boolean flags
		...Object.values(sizes).reduce((acc, s) => ({ ...acc, [s]: Boolean }), {}),
	},
	inject: {
		labeledBy: {
			from: 'formItemId',
			default: null,
		},
	},
	emits: ['update:modelValue', 'change', 'load-more', 'search', 'open'],
	data() {
		return {
			isOpen: false,
			focusedIndex: this.focusFirst ? 0 : -1,
			searchTerm: '',
			isTransitioning: false,
			id: dropdownId++,
			SearchIcon: markRaw(SearchIcon),
			maxHeight: 200,
			childOptions: null,
			selectedOptionsById: {},
		}
	},
	mounted() {
		if (this.multiple && this.modelValue?.length > 0) {
			// if multiple and there are selected options, update the internal state
			this.modelValue.forEach((item) => {
				item.selected = true
				this.selectedOptionsById[item.id] = item
			})
		}
		this.resetFocusedOption()
		window.addEventListener('resize', this.closeAllDropdownsExcept)
		document.addEventListener('click', this.onDocumentClick)

		const { optionsList } = this.$refs
		optionsList?.addEventListener('transitionend', this.onTransitionEnd)
		// if (this.global) {
		// 	// move this element to it's new global parent for proper positioning
		// 	optionsDiv.appendChild(optionsList)
		// 	this.removePositionStyles()
		// }
		// listen for close events
		this.$onEventBus({
			'close-all-dropdowns': this.closeAllDropdownsExcept,
			'clear-dropdown-search': () => {
				// TODO: this is stupid. If this logic should stay permanently, we need to come
				// up with a better solution than using an eventbus event
				if (!this.search) return
				this.searchTerm = ''
				this.$refs.searchInput?.focus()
			},
		})
	},
	beforeUnmount() {
		window.removeEventListener('resize', this.closeAllDropdownsExcept)
		document.removeEventListener('click', this.onDocumentClick)
		removeEscapeListener(this.escapeId)
		this.close()
		// this.$refs.optionsList?.removeEventListener(
		// 	'transitionend',
		// 	this.onTransitionEnd
		// )
		// const { optionsList, childOptionsList } = this.$refs
		// if (optionsList) {
		// 	this.$refs.optionsList.removeEventListener(
		// 		'transitionend',
		// 		this.onTransitionEnd
		// 	)
		// 	optionsList.remove()
		// }
		// if (childOptionsList) {
		// 	childOptionsList.remove()
		// }
	},
	computed: {
		value() {
			return this.modelValue?.id ?? this.modelValue ?? null
		},
		selected() {
			if (this.value === null) return null
			let selectedOption = null
			for (const option of this.options) {
				if (this.value === (option.id ?? option)) {
					selectedOption = option
					break
				}
				if (option.childOptions) {
					for (const child of option.childOptions) {
						if (this.value === (child.id ?? child)) {
							selectedOption = child
							break
						}
					}
				}
			}
			return selectedOption
		},
		isEmpty() {
			return !this.loading && !this.loadingMore && !this.optionsToDisplay.length
		},
		uniqueId() {
			return this._.uid
		},
		hasChildOptions() {
			return this.options?.some?.((o) => o.childOptions?.length > 0)
		},
		optionsToDisplay() {
			if (!this.filterFn || !this.searchTerm) return this.options
			const regex = new RegExp(escapeRegExp(this.searchTerm), 'i')
			return this.options.filter((item) => this.filterFn(item, regex))
		},
	},
	watch: {
		isOpen(isOpen) {
			this.isTransitioning = true
			this.$emit('open', isOpen)
			if (isOpen) {
				this.escapeId = addEscapeListener(() => {
					this.isOpen = false
				})
			} else {
				removeEscapeListener(this.escapeId)
			}
		},
		modelValue(value) {
			if (this.multiple) {
				this.selectedOptionsById = {}
				if (value?.length > 0) {
					// if multiple and there are selected options, update the internal state
					value.forEach((item, i) => {
						item.selected = true
						item.order = item.order || i + 1
						this.selectedOptionsById[item.id] = item
					})
				}
			}
		},
		optionsToDisplay(list, old) {
			if (this.focusFirst && list[0]?.id !== old[0]?.id) {
				this.resetFocusedOption()
				setTimeout(() => {
					this.setFocusedOption(1)
					if (this.search) this.$refs.searchInput?.focus()
				}, 25)
			}
		},
	},
	methods: {
		onTransitionEnd() {
			this.isTransitioning = false
		},

		onChange(option) {
			if (option.childOptions || option.noHover) return
			if (this.multiple) {
				this.onChangeMultiple(option)
			} else {
				this.$emit('update:modelValue', option.id ?? option)
				this.$emit('change', option.id ?? option)
				this.close()
			}
		},

		onChangeMultiple(option) {
			if (this.selectedOptionsById[option.id]) {
				option.selected = false
				delete option.order
				delete this.selectedOptionsById[option.id]
			} else {
				const otherVals = Object.values(this.selectedOptionsById)
				const order = otherVals.length
					? Math.max(...otherVals.map((o) => o.order)) || 0
					: 0
				option.selected = true
				option.order = order + 1 // allows us to track/maintain the order of selection
				this.selectedOptionsById[option.id] = option
			}
			const values = Object.values(this.selectedOptionsById).sort(
				(a, b) => a.order - b.order
			)
			this.$emit('update:modelValue', values)
			this.$emit('change', values)
		},

		onSearch() {
			this.$emit('search', this.searchTerm, this.optionsToDisplay)
		},

		onSearchEnter() {
			const option = this.optionsToDisplay[this.focusedIndex]
			if (this.search && option && this.searchTerm) {
				this.onChange(option)
			} else if (this.search && option && !this.searchTerm) {
				this.close()
			}
		},

		onDocumentClick(e) {
			const isWithinOptions = e.target.closest('.dropdown-options-container')
			if (!isWithinOptions) {
				this.close()
			}
		},

		async open() {
			if (this.disabled) return
			closeAllDropdowns(this.id)
			this.isOpen = true
			if (this.global) {
				await this.$nextTick()
				this.adjustPosition()
			}
			this.resetFocusedOption()
			await this.$nextTick()
			this.$refs.optionsList.scrollTop = 0
			if (this.search) {
				this.$refs.searchInput.focus()
			} else {
				this.$refs.options.focus()
			}
			// await this.$nextTick()
			// if (this.global && this.$refs.childOptionsList) {
			// 	optionsDiv.appendChild(this.$refs.childOptionsList)
			// }
		},
		close() {
			this.isOpen = false
			this.searchTerm = ''
			this.childOptions = null
			this.removePositionStyles()
			this.resetFocusedOption()
		},
		toggle() {
			this.isOpen ? this.close() : this.open()
		},
		setFocusedOption(indexOffset = 1) {
			const list = this.optionsToDisplay
			this.focusedIndex += indexOffset
			if (this.focusedIndex < 0) {
				this.focusedIndex = list.length - 1
			} else if (this.focusedIndex > list.length - 1) {
				this.focusedIndex = 0
			}
			const option = list[this.focusedIndex]
			if (option?.noHover) {
				// if this one is not selectable, just skip to the next one
				return this.setFocusedOption(indexOffset)
			}
			const [optionRef] = this.$refs[`option${this.focusedIndex}`] || []
			optionRef?.focus()
		},
		resetFocusedOption() {
			this.focusedIndex = -1
		},
		onChangeSelectedOption(e) {
			switch (e.key) {
				case 'ArrowDown':
				case 'ArrowRight':
					e.preventDefault()
					this.setFocusedOption(1)
					break
				case 'ArrowUp':
				case 'ArrowLeft':
					e.preventDefault()
					this.setFocusedOption(-1)
					break
			}
		},
		adjustPosition() {
			if (!this.global) return
			const { selectedOption, optionsList } = this.$refs
			const rect = selectedOption.getBoundingClientRect()
			const { clientHeight } = document.documentElement
			// logic to handle the dropdown colliding with the bottom of the screen
			if (rect.top + rect.height + this.maxHeight > clientHeight) {
				Object.assign(optionsList.style, {
					top: '', // we don't have a top property because we don't know the height of our optionsList
					bottom: `${clientHeight - rect.top}px`,
					left: `${rect.left}px`,
					minWidth: `${selectedOption.offsetWidth}px`,
				})
			} else {
				Object.assign(optionsList.style, {
					top: `${rect.top + rect.height}px`,
					left: `${rect.left}px`,
					minWidth: `${selectedOption.offsetWidth}px`,
				})
			}
		},
		removePositionStyles() {
			if (!this.global || !this.$refs.optionsList) return
			Object.assign(this.$refs.optionsList.style, {
				top: '-9999px',
				left: '-9999px',
				bottom: '',
			})
			this.removeChildPositionStyles()
		},
		removeChildPositionStyles() {
			if (this.$refs.childOptionsList) {
				Object.assign(this.$refs.optionsList.style, {
					top: '-9999px',
					left: '-9999px',
				})
			}
		},
		closeAllDropdownsExcept(exceptId) {
			if (exceptId === this.id || !this.isOpen) return
			this.close()
		},
		setChildOptions(option, index) {
			const optionElem = this.$refs[`option${index}`]?.[0]
			if (this.childOptions) {
				this.childOptions = null
				optionElem.blur()
				this.removeChildPositionStyles()
				return
			}
			if (!option?.childOptions) return
			this.childOptions = option.childOptions
			if (!optionElem) return
			// move child options to the correct position based on selected option
			const optionRect = optionElem.getBoundingClientRect()
			const mainRect = this.$refs.optionsList.getBoundingClientRect()
			if (this.global) {
				Object.assign(this.$refs.childOptionsList.style, {
					top: `${optionRect.bottom}px`,
					left: `${optionRect.left + 32}px`,
				})
			} else {
				const offset = optionRect.bottom - mainRect.y + optionRect.height - 16
				this.$refs.childOptionsList.style.top = `${offset}px`
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.dropdown {
	&.form-item-success {
		.dropdown-selected {
			border-color: var(--teal-300);
		}
	}
	&.form-item-error {
		.dropdown-selected {
			border-color: var(--red-200);
		}
	}
}

.dropdown-container,
.dropdown-parent {
	position: relative;
	width: 100%;
	&.small {
		.dropdown-selected {
			font-size: 14px;
			height: 34px;
		}
	}
	&.large {
		.dropdown-selected {
			font-size: 18px;
			height: 54px;
		}
	}
	&.disabled {
		.dropdown-selected {
			cursor: not-allowed;
			.dropdown-selected-content {
				opacity: 0.7;
			}
		}
	}
	&.pill {
		.dropdown-selected {
			border-radius: 100px;
		}
	}
	&.open {
		.dropdown-selected {
			border-color: var(--blue-300);
		}
	}
	&.hideCaret {
		.dropdown-selected {
			.dropdown-selected-content {
				max-width: 100%;
			}
		}
	}
	.dropdown-selected {
		@include flex-spread(4px);
		align-items: center;
		border: 1px solid var(--neutral-200);
		border-radius: 6px;
		height: 40px;
		padding: 0 12px;
		cursor: pointer;
		width: 100%;
		background: #fafafa;
		color: var(--neutral-400);
		position: relative;
		.dropdown-selected-content {
			@include flex-center;
			max-width: calc(100% - 16px);
			> svg {
				width: 18px;
				height: 18px;
				margin-right: 8px;
			}
		}
		.dropdown-selected-label {
			@include ellipsis();
			flex-grow: 1;
			color: var(--neutral-800);
		}
		.dropdown-selected-loading {
			@include absolute-cover;
			@include flex-center;
			border-radius: 6px;
			background: var(--neutral-100);
			:deep(.ios-spinner),
			#mainSpinner {
				background: transparent !important;
				width: 30px !important;
				height: 30px !important;
			}
		}
		.dropdown-icon {
			@include flex-center;
			font-size: 20px;
			margin-right: 8px;
		}
	}
	.dropdown-action {
		padding: 8px;
		border-bottom: 1px solid var(--neutral-200);
	}
}

.right-content {
	display: flex;
}

.dropdown-badge {
	display: block;
	padding: 0.2em 0.6em;
	border-radius: 16px;
	background-color: var(--blue-300);
	font-family: Avenir-Black;
	font-size: 12px;
	color: #fff;
	margin-right: 8px;
}

.chevron {
	font-size: 14px;
	vertical-align: middle;
}
.search-icon {
	font-size: 14px;
	margin-left: 10px;
	color: var(--neutral-400);
}

.default-empty-state {
	width: 100%;
	padding: 30px;
	text-align: center;
	color: var(--neutral-400);
}

.loading-overlay {
	@include absolute-cover;
	@include flex-center;
	background: white;
	:deep(.ios-spinner),
	#mainSpinner {
		width: 30px !important;
		height: 30px !important;
	}
}

.loading-overlay-search {
	top: 40px;
}

.loading-more {
	@include flex-center;
	:deep(.ios-spinner),
	#mainSpinner {
		width: 30px !important;
		height: 30px !important;
	}
}

:deep(.load-spinner) {
	left: auto;
	transform: none;
}

.dropdown-options-container {
	z-index: 100;
	position: absolute;
	width: 100%;
	max-height: 0;
	overflow: hidden;
	background: transparent;
	transition: all 0.3s ease-in-out;
	transition-property: max-height, border;
	outline: none;
	border: 1px solid transparent;
	border-radius: 6px;
	box-shadow: 0 4px 14px 0px rgba(0, 0, 0, 0.13);
	&.global {
		position: fixed;
		width: max-content;
		@include max-z(1);
	}
	&.open {
		max-height: 200px;
		background: white;
		border-color: var(--blue-300);
		.dropdown-search {
			display: block;
		}
	}
	&.transition {
		background: white;
	}
	&.transition,
	&.scrollable {
		@include flex-column;
		.scroller {
			max-height: 200px;
			overflow: auto;
			flex-grow: 1;
		}
	}
	&.search {
		@include flex-column;
	}
	&.multiple {
		.dropdown-option {
			.pronto-checkbox + .dropdown-option-content {
				max-width: calc(100% - 32px);
			}
		}
	}
	&.child-options-container {
		overflow-x: hidden;
		overflow-y: auto;
		&.global {
			top: -9999px;
			left: -9999px;
			@include max-z(2);
		}
	}
	.dropdown-options {
		outline: none;
	}
	.dropdown-options,
	.dropdown-action {
		position: relative;
		padding: 8px;
	}
	.dropdown-option,
	.dropdown-action > * {
		padding: 8px;
		border-radius: 6px;
		color: var(--neutral-800);
		outline: none;
		&:not(.no-hover) {
			cursor: pointer;
			&:hover,
			&:focus,
			&:active,
			&.focused {
				background: $light-blue;
			}
		}
		&.has-children {
			@include flex-spread;
		}
	}
	.dropdown-option {
		@include flex(12px);
		align-items: start;
		.dropdown-option-content {
			flex-grow: 1;
			max-width: 100%;
		}
	}
	.dropdown-search {
		display: none;
		height: 44px;
		padding-top: 4px;
		background-color: white;
		:deep(.pronto-input) {
			border: none;
			background-color: white;
			border-bottom: 1px solid var(--neutral-200);
			border-bottom-left-radius: 0;
			border-bottom-right-radius: 0;
		}
	}
}

.dropdown-overlay {
	position: fixed;
	z-index: 99;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
}
</style>

<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.dropdown-container,
	.dropdown-parent {
		.dropdown-selected {
			background: var(--neutral-100) !important;
			.selected-option {
				color: var(--neutral-800) !important;
			}
			.dropdown-selected-loading {
				background: var(--neutral-200) !important;
			}
		}
	}
	.dropdown-options-container {
		border: 1px solid transparent !important;
		&.open,
		&.transition {
			background: var(--neutral-200) !important;
			border: 1px solid var(--neutral-700) !important;
		}
		.loading-overlay {
			background: var(--neutral-200) !important;
		}
		.dropdown-search {
			background-color: var(--neutral-200);
			.pronto-input {
				background-color: var(--neutral-200);
			}
		}
	}
}
</style>
